import React, { FC, Fragment } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableSortLabel } from '@mui/material';
import { OrderBy } from 'models/search-result';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import LabItem from './lab-item.component';
import { Lab } from 'models/lab';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type LabListProps = {
    filteredLabs: Lab[];
    onFetchMore: () => void;
    onSetOrderOnField: (field: string) => void;
    orderBy: OrderBy;
    onEditLabClick: (lab: Lab) => void;
    onTransportSettingClick: (lab: Lab) => void;
    onHHSyncClick?: (clinic: Lab) => void;
    onTemplateClick: (lab: Lab) => void;
};
const LabList: FC<LabListProps> = ({
    filteredLabs,
    onFetchMore,
    onSetOrderOnField,
    orderBy,
    onEditLabClick,
    onTransportSettingClick,
    onHHSyncClick,
    onTemplateClick,
}: LabListProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'organization-numbers']);

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, filteredLabs?.length || 0);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={filteredLabs ? filteredLabs.length : 0}
                    next={onFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{translate('common:STATUS')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => onSetOrderOnField('name')}
                                        active={orderBy.orderBy === 'name'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('common:NAME')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => onSetOrderOnField('organizationNumber')}
                                        active={orderBy.orderBy === 'organizationNumber'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('organization-numbers:SE_ORGANIZATION_NUMBER')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('organization:ADDRESS:ADDRESS')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => onSetOrderOnField('created.on')}
                                        active={orderBy.orderBy === 'created.on'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('common:CREATED')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLabs ? (
                                <Fragment>
                                    {filteredLabs.map((lab: Lab, index: number) => (
                                        <LabItem
                                            key={index}
                                            lab={lab}
                                            onEditClinicClick={onEditLabClick}
                                            onTransportSettingClick={onTransportSettingClick}
                                            onHHSyncClick={onHHSyncClick}
                                            onTemplateClick={onTemplateClick}
                                        />
                                    ))}
                                </Fragment>
                            ) : undefined}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
        </Fragment>
    );
};

export default LabList;
